import 'keen-slider/keen-slider.min.css'

import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { ZoomIn } from 'app/components/Common/Animation/ZoomIn'
import { Arrow } from 'app/components/Common/Arrow'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { Dot } from 'app/components/Common/Dot'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { Shape } from 'app/components/Common/Shape'
import { Spinner } from 'app/components/Common/Spinner'
import { Rumble } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { Media } from 'app/theme/media'
import { useKeenSlider } from 'keen-slider/react'
import { uniq } from 'lodash'
import React, { memo, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  cta?: ButtonProps
  description?: string
  images: ImageProps[]
  thumb?: ImageProps
  title?: string
}

export const Location = memo(function Location({
  cta,
  description,
  images,
  thumb,
  title,
}: Props) {
  if (!images) {
    return null
  }
  if (images.length < 1) {
    return null
  }

  const [animObjectRef, isAnimObjectVisible] = useInView({
    triggerOnce: true,
  })

  const [currentSlide, setCurrentSlide] = useState(0)
  const [details, setDetails] = useState<any>(null)
  const [loaded, setLoaded] = useState<boolean[]>([])
  const [nextLoaded, setNextLoaded] = useState<boolean[]>([])

  function positionStyle(idx: any) {
    if (!details) {
      return undefined
    }

    const winWidth = window.innerWidth

    const position = details.slides[idx].distance
    const x = (instanceRef.current?.size || winWidth) * -position
    return {
      transform: `translateX(${x + position * (winWidth / 2)}px)`,
      WebkitTransform: `translateX(${x + position * (winWidth / 2)}px)`,
    }
  }

  const [sliderRef, instanceRef] = useKeenSlider({
    defaultAnimation: {
      duration: 1800,
    },
    drag: false,
    loop: images.length > 1 ? true : false,
    animationStarted(s) {
      setCurrentSlide(s.track.details.rel)
    },
    detailsChanged(s) {
      images.length > 1 ? setDetails(s.track.details) : null
    },
    breakpoints: {
      '(max-width: 1199px)': {
        drag: true,
      },
    },
  })

  useEffect(() => {
    const newLoaded = [...loaded]
    newLoaded[currentSlide] = true
    const newNextLoaded = [...nextLoaded]
    newNextLoaded[currentSlide + 1] = true

    setLoaded(newLoaded)
    setNextLoaded(newNextLoaded)
  }, [currentSlide, instanceRef, sliderRef])

  return (
    <Container>
      <Media greaterThanOrEqual="desktopSmall">
        <Shapes>
          <Shape variant={3} />
          <Shape variant={3} />
          <Shape variant={3} />
        </Shapes>

        <FadeInUp>
          <Counter>
            {`${currentSlide + 1}/${images.length}`}

            <Rumble />
          </Counter>
        </FadeInUp>
      </Media>

      <Wrapper
        className={isAnimObjectVisible ? 'visible' : undefined}
        ref={animObjectRef}
      >
        <Spinner variant="invert" />

        <Carousel ref={sliderRef}>
          {uniq(images).map((item, index) => (
            <Slide
              key={index}
              className={`keen-slider__slide${
                currentSlide === index ? ' active' : ''
              }`}
            >
              <Inner style={positionStyle(index)}>
                <Image
                  alt={loaded[index] || nextLoaded[index] ? item.alt : ''}
                  {...(loaded[index] || nextLoaded[index] ? item : null)}
                />
              </Inner>
            </Slide>
          ))}
        </Carousel>

        {images.length > 1 ? (
          <Media greaterThanOrEqual="desktopSmall">
            <Arrows dial={5} row wrap>
              <Arrow
                onClick={(e) =>
                  e.stopPropagation() || instanceRef.current?.prev()
                }
              />

              <Arrow
                direction="R"
                onClick={(e) =>
                  e.stopPropagation() || instanceRef.current?.next()
                }
              />
            </Arrows>
          </Media>
        ) : null}
      </Wrapper>

      {images.length > 1 ? (
        <Media lessThan="desktopSmall">
          <Dots dial={5} row>
            {images.map((_item, index) => (
              <Dot
                key={index}
                className={currentSlide === index ? 'active' : undefined}
                onClick={() => {
                  instanceRef.current?.moveToIdx(index)
                }}
              />
            ))}
          </Dots>
        </Media>
      ) : null}

      {thumb ? (
        <Media greaterThanOrEqual="desktopSmall">
          <Thumb>
            <ZoomIn>
              <Image {...thumb} />
            </ZoomIn>
          </Thumb>
        </Media>
      ) : null}

      <Text>
        {title ? <Title text={title} /> : null}

        {description ? (
          <FadeInUp>
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          </FadeInUp>
        ) : null}

        {cta ? (
          <FadeInUp>
            <CTA {...cta} variant="flat-invert" />
          </FadeInUp>
        ) : null}
      </Text>
    </Container>
  )
})

const Container = styled.section`
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  margin-top: 13.125rem;
  padding-bottom: 12.5rem;
  position: relative;

  @media (max-width: 1199px) {
    margin-top: 5.625rem;
    padding-bottom: 5.625rem;
  }
`

const Shapes = styled.div`
  position: absolute;
  bottom: 17.4375rem;
  left: calc(20.347vw + 1.25rem);
`

const Wrapper = styled.div`
  width: 75vw;
  height: 85vh;
  max-height: 50rem;
  clip-path: inset(0% 0% 100% 0%);
  overflow: hidden;
  position: relative;
  transition: 1s 0.3s ease-in-out;
  z-index: 2;
  &.visible {
    clip-path: inset(0);
  }

  @media (max-width: 1199px) {
    aspect-ratio: 1.33;
    width: 100%;
    height: auto;
    max-height: none;
  }
`

const Counter = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.25rem;
  font-weight: 300;
  line-height: 2.875rem;
  position: absolute;
  top: 10.75rem;
  left: 81.944vw;

  svg {
    display: block;
    width: 0.375rem;
    height: 0.375rem;
    fill: ${({ theme }) => theme.colors.variants.neutralLight5};
    margin: 1.25rem auto;
  }
`

const Carousel = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralLight5};
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
`

const Slide = styled.div`
  position: relative;
  overflow: hidden;
  &.active {
    img {
      transition-delay: 0s;
      transform: scale(1);
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    transition: transform 0.8s cubic-bezier(0.25, 1, 0.5, 1);
    transform-origin: center left;
    transform: none;
    transform: scale(1.3);
    z-index: 3;
  }

  @media (max-width: 1199px) {
    &.active {
      img {
        transform: none !important;
      }
    }

    img {
      transform: none;
    }
  }
`

const Inner = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;

  @media (max-width: 1199px) {
    transition-timing-function: cubic-bezier(0.85, 0, 0.15, 1);
  }
`

const Arrows = styled(FlexBox)`
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 3;
`

const Dots = styled(FlexBox)`
  margin-top: 1.25rem;
`

const Thumb = styled.div`
  aspect-ratio: 0.75;
  width: 29.167vw;
  position: absolute;
  top: 26.25rem;
  right: 8.333vw;

  > div {
    height: 100%;
    position: relative;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }
`

const Text = styled.div`
  max-width: 39.583vw;
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  margin-top: 6.6875rem;
  padding-left: 8.333vw;
  position: relative;
  z-index: 3;

  @media (max-width: 1199px) {
    max-width: none;
    margin-top: 2.5rem;
    padding: 0 1.875rem;
  }
`

const Title = styled(AnimatedTitle)`
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 3rem;
  font-weight: 100;
  line-height: 3.875rem;

  b,
  strong {
    font-family: ${({ theme }) => theme.fontFamily.heading};
    font-size: 3.25rem;
    font-weight: 300;
  }

  @media (max-width: 1199px) {
    font-size: 2.25rem;
    line-height: 2.25rem;

    b,
    strong {
      font-size: 2.25rem;
    }
  }
`

const Description = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  margin-top: 3.75rem;

  p {
    margin-block-end: 1.5em;
  }

  @media (max-width: 1199px) {
    margin-top: 1.875rem;
  }
`

const CTA = styled(Button)`
  margin-top: 3.75rem;

  @media (max-width: 1199px) {
    margin-top: 3.125rem;
  }
`
